import { CryptoInitializationError, INCORRECT_MASTER_KEY } from "./cryptoErrors";

const VERSION = "v2";
const PREFIX = `${VERSION}:`;
const SIA_ID_STORAGE_KEY = "sia.benchmarking.siaId";
const SECRET_KEY = process.env.REACT_APP_BENCHMARKING_KEY || "";
const V2_VALIDATION_SALT = "sia.benchmarking.webcrypto.validation.v2";
const V2_ENCRYPTION_SALT = "sia.benchmarking.webcrypto.encryption.v2";
const V2_KDF_ITERATIONS = 250000;

const encode = (value) => new TextEncoder().encode(value);
const decode = (value) => new TextDecoder().decode(value);
const toHex = (bytes) => Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
const fromHex = (value) => {
  if (!value || value.length % 2 || /[^0-9a-f]/i.test(value)) throw new Error("Invalid hexadecimal value");
  return new Uint8Array(value.match(/.{2}/g).map((byte) => Number.parseInt(byte, 16)));
};
const webCrypto = () => {
  const crypto = globalThis.crypto;
  if (!crypto?.subtle) throw new CryptoInitializationError("Web Crypto API is unavailable. Use HTTPS and a supported browser.");
  return crypto;
};
const sha256Hex = async (value) => {
  const digest = await webCrypto().subtle.digest("SHA-256", encode(value));
  return toHex(new Uint8Array(digest));
};

export class CryptoEngineV2 {
  static VERSION = VERSION;

  constructor(masterKey) {
    if (!masterKey) throw new CryptoInitializationError("CryptoEngineV2: missing master key");
    this.version = VERSION;
    this.masterKey = masterKey;
    this.siaId = sessionStorage.getItem(SIA_ID_STORAGE_KEY) || "";
    this.validationHash = null;
    this.companyHash = null;
    this.encryptionKeyPromise = null;
    this.purged = false;
    this.readyPromise = this.initialize();
  }

  async initialize() {
    const validationHash = `${PREFIX}${await this.deriveBitsHex(V2_VALIDATION_SALT)}`;
    const companyHash = `${PREFIX}${await sha256Hex(`${this.siaId}${SECRET_KEY}${validationHash}`)}`;
    if (this.purged) return;
    this.validationHash = validationHash;
    this.companyHash = companyHash;
  }

  async ready() {
    await this.readyPromise;
    if (this.purged || !this.masterKey || !this.validationHash || !this.companyHash)
      throw new CryptoInitializationError("CryptoEngineV2: engine is unavailable");
    return this;
  }

  async deriveBitsHex(salt) {
    if (this.purged || !this.masterKey) throw new CryptoInitializationError("CryptoEngineV2: engine is unavailable");
    const subtle = webCrypto().subtle;
    const baseKey = await subtle.importKey("raw", encode(this.masterKey), "PBKDF2", false, ["deriveBits"]);
    const bits = await subtle.deriveBits(
      { name: "PBKDF2", salt: encode(salt), iterations: V2_KDF_ITERATIONS, hash: "SHA-256" },
      baseKey,
      256
    );
    return toHex(new Uint8Array(bits));
  }

  async getValidationHash() {
    await this.ready();
    return this.validationHash;
  }

  async getCompanyHash() {
    await this.ready();
    return this.companyHash;
  }

  async getEncryptionKey() {
    await this.ready();
    if (!this.encryptionKeyPromise) {
      this.encryptionKeyPromise = (async () => {
        const subtle = webCrypto().subtle;
        const baseKey = await subtle.importKey("raw", encode(this.masterKey), "PBKDF2", false, ["deriveKey"]);
        return subtle.deriveKey(
          {
            name: "PBKDF2",
            salt: encode(`${V2_ENCRYPTION_SALT}:${this.siaId}`),
            iterations: V2_KDF_ITERATIONS,
            hash: "SHA-256",
          },
          baseKey,
          { name: "AES-GCM", length: 256 },
          false,
          ["encrypt", "decrypt"]
        );
      })().catch((error) => {
        this.encryptionKeyPromise = null;
        throw error;
      });
    }
    return this.encryptionKeyPromise;
  }

  async encryptEmployee(nameOrNum) {
    await this.ready();
    const value = String(nameOrNum ?? "").trim();
    if (!value) throw new Error("CryptoEngineV2: missing employee name/number");
    const crypto = webCrypto();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const payload = encode(`${this.siaId}${value}*-${Date.now()}-`);
    const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await this.getEncryptionKey(), payload);
    return `${PREFIX}${toHex(iv)}:${toHex(new Uint8Array(encrypted))}`;
  }

  async decryptEmployee(cipherText) {
    await this.ready();
    try {
      const [version, ivHex, cipherHex, ...rest] = String(cipherText || "").split(":");
      if (version !== VERSION || rest.length || ivHex?.length !== 24 || !cipherHex) return INCORRECT_MASTER_KEY;
      const plainBuffer = await webCrypto().subtle.decrypt(
        { name: "AES-GCM", iv: fromHex(ivHex) },
        await this.getEncryptionKey(),
        fromHex(cipherHex)
      );
      const scoped = decode(plainBuffer);
      if (this.siaId && !scoped.startsWith(this.siaId)) return INCORRECT_MASTER_KEY;
      const value = (this.siaId ? scoped.slice(this.siaId.length) : scoped).split("*")[0];
      return value || INCORRECT_MASTER_KEY;
    } catch (error) {
      return INCORRECT_MASTER_KEY;
    }
  }

  purge() {
    this.purged = true;
    this.masterKey = null;
    this.validationHash = null;
    this.companyHash = null;
    this.encryptionKeyPromise = null;
  }
}
